import React,{useMemo,useState,useEffect} from 'react';
import {useLocation,useNavigate,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useApp} from '../contexts/AppContext';
import {accountingEntries,selectAccountingEntries} from '../services/financialLedger';
import {matchesSearch} from '../services/smartSearch.js';
import {sumMoney,toCents,fromCents,calendarDate} from '../services/financialMath';
import {buildBudgetInsights} from '../services/budgetInsights';
import {startOfMonth,endOfMonth,format,addMonths,parseISO} from 'date-fns';
import * as Fi from 'react-icons/fi';

// All values in this redesigned page derive from existing records and the
// established budgetInsights and financialLedger services. No data migration.
const card='rounded-2xl border border-[#dce5f0] bg-white shadow-[0_4px_18px_rgba(15,43,82,.045)]';
const categoryIcons={
 Groceries:Fi.FiShoppingCart,'Dining Out':Fi.FiCoffee,Transport:Fi.FiTruck,
 Utilities:Fi.FiZap,Entertainment:Fi.FiFilm,Shopping:Fi.FiShoppingBag,
 Healthcare:Fi.FiHeart,Education:Fi.FiBookOpen,Insurance:Fi.FiShield,
 'Rent/Mortgage':Fi.FiHome,Housing:Fi.FiHome,Subscriptions:Fi.FiTv,
 'Gym/Fitness':Fi.FiActivity
};
const defaultColors=['#079ce7','#8648f6','#32c865','#ffba28','#ff6572','#9caabf'];
const attentionStatuses=new Set(['warning','forecast-over','over']);
const atRiskStatuses=new Set(['forecast-over','over']);

const usageColor=(row)=>atRiskStatuses.has(row.status)?'#ef4f5f':
 row.status==='warning'?'#fb923c':row.status==='no-limit'?'#94a3b8':'#4ccd75';
const usageTone=(row)=>atRiskStatuses.has(row.status)?'bg-rose-50 text-rose-700':
 row.status==='warning'?'bg-orange-50 text-orange-700':
 row.status==='no-limit'?'bg-slate-100 text-slate-700':'bg-emerald-50 text-emerald-700';

const shortDate=(value)=>{
 try{const iso=calendarDate(value);return iso?format(parseISO(iso),'MMM d, yyyy'):String(value||'—');}
 catch{return String(value||'—');}
};

function SectionHeading({Icon,title,subtitle,action}){
 return <div className="flex flex-wrap items-start justify-between gap-3">
   <div className="flex items-center gap-3">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-sky-50 text-[#0176D3]"><Icon size={22}/></span>
    <div><h2 className="text-base font-extrabold text-slate-950">{title}</h2>
     <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p></div>
   </div>
   {action}
 </div>;
}

function BudgetMetric({Icon,title,value,foot,color,tint,action}){
 return <article className={card+' flex min-w-0 items-center gap-3 px-4 py-4 sm:px-5'}>
  <span className={'grid h-12 w-12 shrink-0 place-items-center rounded-full '+tint}><Icon size={24}/></span>
  <div className="min-w-0 flex-1">
   <p className="text-xs font-semibold text-slate-700">{title}</p>
   <p className={'mt-1 truncate text-xl font-extrabold tabular-nums sm:text-2xl '+color} title={value}>{value}</p>
   {foot&&<p className="mt-1 text-[11px] font-semibold text-slate-500">{foot}</p>}
  </div>
  {action}
 </article>;
}

export default function Budgets(){
 const {state,actions}=useApp();
 const {budgets=[],transactions=[],ious=[],categories=[],settings}=state;
 const location=useLocation(),navigate=useNavigate();
 const [showAddForm,setShowAddForm]=useState(Boolean(location.state?.openAddBudget));
 const [offset,setOffset]=useState(0);
 const [filter,setFilter]=useState('all');
 const [search,setSearch]=useState(()=>new URLSearchParams(location.search).get('search')||'');
 useEffect(()=>setSearch(new URLSearchParams(location.search).get('search')||''),[location.search]);
 const changeSearch=value=>{
  setSearch(value);
  const params=new URLSearchParams(location.search);
  if(value.trim())params.set('search',value.trim());
  else params.delete('search');
  navigate({pathname:'/budgets',search:params.toString()?'?'+params.toString():''},{replace:true});
 };
 const [showAlertDetails,setShowAlertDetails]=useState(false);
 // Dashboard's Add Budget shortcut opens the existing form; consume the hint
 // without retaining it in browser history. Other navigation remains unchanged.
 useEffect(()=>{
  if(location.state?.openAddBudget){
   navigate(location.pathname,{replace:true,state:null});
  }
 },[location.pathname,location.state,navigate]);

 const month=addMonths(new Date(),offset);
 const start=startOfMonth(month),end=endOfMonth(month);
 const insights=useMemo(()=>buildBudgetInsights({
   budgets,transactions,ious,asOf:new Date(),monthDate:month,
   fiscalYearStart:settings.financialYearStart,
   warningPercent:settings.budgetWarningPercent||80
 }),[budgets,transactions,ious,offset,settings.financialYearStart,settings.budgetWarningPercent]);
 const money=value=>new Intl.NumberFormat('en-AU',{
  style:'currency',currency:settings.currency||'AUD'
 }).format(Number(value)||0);
 const totalBudget=sumMoney(insights,item=>item.limit);
 const totalSpent=sumMoney(insights,item=>item.spent);
 const remaining=fromCents(toCents(totalBudget)-toCents(totalSpent));
 const percent=totalBudget?Math.round(totalSpent/totalBudget*100):0;
 const rows=insights.map((item,index)=>{
   const category=categories.find(c=>c.name===item.category);
   const color=category?.color||defaultColors[index%defaultColors.length];
   return {...item,color,Icon:categoryIcons[item.category]||Fi.FiTag,
     percentage:item.limit>0?item.ratio*100:0};
 });
 const onTrack=rows.filter(row=>row.status==='on-track');
 const warning=rows.filter(row=>row.status==='warning');
 const forecastOver=rows.filter(row=>row.status==='forecast-over');
 const over=rows.filter(row=>row.status==='over');
 const alerts=rows.filter(row=>attentionStatuses.has(row.status));
 const critical=[...over,...forecastOver];
 const filteredRows=rows.filter(row=>matchesSearch([row.category,row.period,row.limit],search)&&(
   filter==='all'
   ?true:filter==='on-track'
   ?row.status==='on-track':filter==='near'
   ?row.status==='warning':attentionStatuses.has(row.status)));
 const filterDefs=[
   {value:'all',label:'All',count:rows.length},
   {value:'on-track',label:'On Track',count:onTrack.length},
   {value:'near',label:'Near Limit',count:warning.length},
   {value:'at-risk',label:'At Risk',count:alerts.length}
 ];
 const chartRows=rows.filter(row=>row.spent>0);
 const chartTotal=chartRows.reduce((n,row)=>n+toCents(row.spent),0);
 let acc=0;
 const gradient=chartRows.length&&chartTotal>0?'conic-gradient('+
   chartRows.map(row=>{
     const from=acc;acc+=toCents(row.spent)/chartTotal*100;
     return row.color+' '+from+'% '+acc+'%';
   }).join(',')+')':'conic-gradient(#e2e8f0 0% 100%)';
 const activity=useMemo(()=>selectAccountingEntries(
   accountingEntries(transactions,'accrual',ious),
   {start,end,view:'current',mode:'accrual',asOf:new Date()}
 ).filter(t=>t.type==='expense'&&!t.isVirtualAllocation)
  .sort((a,b)=>String(calendarDate(b.date)).localeCompare(String(calendarDate(a.date))))
  .slice(0,5),[transactions,ious,offset]);
 const alertText=critical.length
   ?critical.slice(0,3).map(x=>x.category+' ('+(x.status==='over'?'over budget':'forecast over budget')+')').join(', ')
   :warning.slice(0,3).map(x=>x.category+' ('+Math.round(x.percentage)+'% used)').join(', ');
 const showForm=()=>setShowAddForm(true);
 const changeMonth=event=>{
   const [year,number]=String(event.target.value).split('-').map(Number);
   if(!year||number<1||number>12)return;
   const today=new Date();
   setOffset((year-today.getFullYear())*12+(number-1-today.getMonth()));
 };
 const matchBudget=transaction=>rows.find(row=>row.category===transaction.category);

 return <motion.div initial={{opacity:0}} animate={{opacity:1}} className="space-y-4 pb-12 md:space-y-5">
  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
   <div><h1 className="text-3xl font-extrabold tracking-tight text-slate-950">Budgets</h1>
    <p className="mt-1 text-sm text-slate-600">Plan, track, and stay in control of your spending by category.</p>
   </div>
   <div className="flex flex-wrap items-center gap-2">
    <button type="button" onClick={showForm}
      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#079de2] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#0176D3] focus-visible:ring-2 focus-visible:ring-blue-400">
      <Fi.FiPlus size={18}/> Add Budget</button>
    <div className="flex min-h-11 items-center gap-1 rounded-xl border border-[#dce5f0] bg-white px-1 shadow-sm">
      <button type="button" aria-label="Previous month" title="Previous month"
       onClick={()=>setOffset(value=>value-1)} className="grid h-10 w-9 place-items-center rounded-lg text-[#385575] hover:bg-blue-50">
       <Fi.FiChevronLeft size={17}/></button>
      <Fi.FiCalendar size={16} className="shrink-0 text-[#385575]"/>
      <label className="sr-only" htmlFor="budget-month">Budget month</label>
      <input id="budget-month" type="month" aria-label="Budget month" value={format(month,'yyyy-MM')}
       onChange={changeMonth} className="w-[137px] min-w-0 bg-transparent py-2 text-xs font-bold text-slate-800 outline-none sm:w-[150px]"/>
      <button type="button" aria-label="Next month" title="Next month"
       onClick={()=>setOffset(value=>value+1)} className="grid h-10 w-9 place-items-center rounded-lg text-[#385575] hover:bg-blue-50">
       <Fi.FiChevronRight size={17}/></button>
    </div>
   </div>
  </div>

  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
   <BudgetMetric Icon={Fi.FiLayers} title="Total Budget" value={money(totalBudget)}
    tint="bg-sky-50 text-[#0176D3]" color="text-emerald-700"/>
   <BudgetMetric Icon={Fi.FiTrendingDown} title="Total Spent" value={money(totalSpent)}
    foot={totalBudget?percent+'% of budget':'No budget limit set'}
    tint="bg-rose-50 text-rose-600" color="text-rose-600"/>
   <BudgetMetric Icon={Fi.FiArrowUpRight} title="Remaining" value={money(remaining)}
    foot={totalBudget?Math.round(100-percent)+'% remaining · not cash available':'No budgets set yet'}
    tint="bg-emerald-50 text-emerald-700"
    color={remaining<0?'text-rose-600':'text-emerald-700'}/>
   <BudgetMetric Icon={Fi.FiAlertTriangle} title="Categories at Risk"
    value={alerts.length+' of '+rows.length}
    foot={critical.length?critical.length+' over / forecast over':warning.length?'Near warning threshold':'No alerts'}
    tint="bg-orange-50 text-orange-600" color="text-orange-700"
    action={<button type="button" aria-label="Review at risk categories" onClick={()=>setFilter('at-risk')}
      className="grid h-10 w-9 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-orange-50">
      <Fi.FiChevronRight size={20}/></button>}/>
  </div>

  <section aria-label="Budget alerts and forecasts"
    className={'rounded-2xl border px-4 py-3 sm:px-5 '+(alerts.length
      ?'border-orange-200 bg-orange-50 text-orange-900'
      :'border-emerald-100 bg-emerald-50 text-emerald-900')}>
    <div className="flex flex-wrap items-center gap-3">
     <span className={'grid h-11 w-11 shrink-0 place-items-center rounded-full '+(alerts.length
       ?'bg-orange-100 text-orange-700':'bg-emerald-100 text-emerald-700')}>
      {alerts.length?<Fi.FiAlertTriangle size={22}/>:<Fi.FiCheckCircle size={22}/>}
     </span>
     <div className="min-w-0 flex-1">
      <h2 className="text-sm font-extrabold sm:text-base">{alerts.length
        ?'Budget Alert: '+alerts.length+' '+(alerts.length===1?'category needs':'categories need')+' attention'
        :rows.length?'Budget health looks good for this period':'Ready to set your first budget'}</h2>
      <p className="mt-1 text-xs leading-relaxed sm:text-sm">{alerts.length
        ?alertText+'. Review your spending pace and budget limits.'
        :rows.length?'No saved category is currently flagged as near or above its monthly limit.'
        :'Add a budget to start tracking spending, remaining amounts, and projected month-end costs.'}</p>
     </div>
     <button type="button" onClick={()=>alerts.length?setFilter('at-risk'):showForm()}
      className="inline-flex min-h-10 items-center gap-1.5 rounded-xl border border-blue-200 bg-white px-4 py-2 text-xs font-bold text-[#0176D3] hover:bg-blue-50">
      {alerts.length?'Review at Risk Categories':'Add Your First Budget'} <Fi.FiChevronRight/>
     </button>
    </div>
  </section>

  <div className="grid grid-cols-1 items-start gap-4 2xl:grid-cols-12">
   <section className={card+' min-w-0 p-4 sm:p-5 2xl:col-span-8'}>
    <SectionHeading Icon={Fi.FiLayers} title="Category Budgets"
      subtitle={'Your budget performance for '+format(month,'MMMM yyyy')}/>
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
     <label className="relative block w-full sm:max-w-xs">
      <span className="sr-only">Search budget categories</span>
      <Fi.FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8195ad]"/>
      <input type="search" aria-label="Search budget categories" value={search}
       onChange={e=>changeSearch(e.target.value)} placeholder="Search budget categories..."
       className="min-h-11 w-full rounded-xl border border-[#dce5f0] bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"/>
     </label>
     {search&&<button type="button" onClick={()=>changeSearch('')} className="rounded-lg px-3 py-2 text-xs font-bold text-[#0176D3] hover:bg-blue-50">Clear search</button>}
    </div>
    <div className="mt-3 flex flex-wrap gap-2" aria-label="Filter budget categories">
      {filterDefs.map(item=><button type="button" key={item.value} aria-pressed={filter===item.value}
        onClick={()=>setFilter(item.value)}
        className={'min-h-10 rounded-lg px-3 py-2 text-xs font-semibold transition focus-visible:ring-2 focus-visible:ring-blue-400 '+(
          filter===item.value?'bg-[#079de2] text-white shadow-sm':
          'border border-slate-200 bg-white text-slate-600 hover:bg-blue-50')}>
        {item.label} <span className={filter===item.value?'text-white/90':'text-slate-400'}>({item.count})</span>
      </button>)}
    </div>
    {filteredRows.length
      ?<>
        <div className="mt-4 hidden overflow-x-auto xl:block">
         <table className="w-full min-w-[790px] border-collapse text-left text-xs" aria-label="Category budget performance">
          <thead className="bg-[#f2f6fb] text-[10px] font-bold uppercase tracking-wide text-[#475a76]">
           <tr><th scope="col" className="rounded-l-lg px-3 py-3">Category</th>
            <th scope="col" className="px-2 py-3 text-right">Budget Limit</th>
            <th scope="col" className="px-2 py-3 text-right">Spent</th>
            <th scope="col" className="px-2 py-3 text-right" title="Month-end estimate based on spending pace and scheduled accruals">Projected (EOM)</th>
            <th scope="col" className="px-2 py-3 text-right">Remaining</th>
            <th scope="col" className="rounded-r-lg px-3 py-3">Progress</th>
           </tr>
          </thead>
          <tbody>{filteredRows.map(row=><tr key={row.id}
            className={'border-b border-slate-100 '+(atRiskStatuses.has(row.status)?'bg-rose-50/50':row.status==='warning'?'bg-orange-50/40':'')}>
            <td className="px-3 py-3">
             <div className="flex min-w-0 items-center gap-2">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                style={{backgroundColor:row.color+'1b',color:row.color}}><row.Icon size={17}/></span>
              <div className="min-w-0"><p className="truncate font-bold text-slate-950" title={row.category}>{row.category}</p>
                <span className="text-[10px] capitalize text-slate-500">{row.period} budget</span></div>
             </div>
            </td>
            <td className="whitespace-nowrap px-2 py-3 text-right font-semibold text-slate-700">{money(row.limit)}</td>
            <td className="whitespace-nowrap px-2 py-3 text-right font-semibold text-slate-800">{money(row.spent)}</td>
            <td className="whitespace-nowrap px-2 py-3 text-right font-bold text-slate-700">
             {money(row.projected)}{row.isEstimate&&<sup className="ml-0.5 text-sky-700" title="Estimate">~</sup>}
            </td>
            <td className={'whitespace-nowrap px-2 py-3 text-right font-bold '+(row.remaining<0?'text-rose-700':'text-slate-800')}>{money(row.remaining)}</td>
            <td className="px-3 py-3">
             <div className="flex min-w-[128px] items-center gap-2">
              <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200">
               <div className="h-full rounded-full" style={{width:Math.max(0,Math.min(100,row.percentage))+'%',backgroundColor:usageColor(row)}}/>
              </div>
              <span className={'min-w-10 shrink-0 rounded-full px-2 py-1 text-center text-[10px] font-extrabold tabular-nums '+usageTone(row)}>
               {row.limit>0?Math.round(row.percentage)+'%':'N/A'}</span>
             </div>
            </td>
           </tr>)}</tbody>
         </table>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-2 2xl:hidden">
         {filteredRows.map(row=><article key={row.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5">
          <div className="flex items-center gap-3">
           <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
             style={{backgroundColor:row.color+'1b',color:row.color}}><row.Icon size={19}/></span>
           <div className="min-w-0 flex-1"><p className="truncate text-sm font-extrabold text-slate-950">{row.category}</p>
            <p className="mt-0.5 text-xs capitalize text-slate-500">{row.period} budget</p></div>
           <span className={'rounded-full px-2 py-1 text-[10px] font-extrabold '+usageTone(row)}>
            {row.limit>0?Math.round(row.percentage)+'%':'N/A'}</span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
           {[
             ['Budget Limit',money(row.limit)],['Spent',money(row.spent)],
             ['Projected (EOM)',money(row.projected)+(row.isEstimate?' ~':'')],
             ['Remaining',money(row.remaining)]
           ].map(([label,value])=><div key={label}>
             <dt className="text-[11px] text-slate-500">{label}</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-800">{value}</dd>
           </div>)}
          </dl>
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-200">
           <div className="h-full rounded-full" style={{width:Math.max(0,Math.min(100,row.percentage))+'%',backgroundColor:usageColor(row)}}/>
          </div>
         </article>)}
        </div>
       </>
      :<div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
        <Fi.FiTarget className="mx-auto text-slate-400" size={32}/>
        <h3 className="mt-3 text-sm font-bold text-slate-900">{rows.length?'No budgets match this filter':'No budgets yet'}</h3>
        <p className="mt-1 text-xs text-slate-500">{rows.length?'Try another status filter.':'Create a budget to start tracking your spending.'}</p>
        {!rows.length&&<button type="button" onClick={showForm}
          className="mt-4 min-h-10 rounded-xl bg-[#0176D3] px-4 py-2 text-xs font-bold text-white">Add Your First Budget</button>}
       </div>}
    <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
      Projected (EOM) is an estimate based on recorded spending pace and scheduled accruals; it does not represent money in a bank account.
      {' '}~ identifies an estimated value.
    </p>
   </section>

   <section className={card+' min-w-0 p-4 sm:p-5 2xl:col-span-4'}>
     <SectionHeading Icon={Fi.FiPieChart} title="Spending by Budget Category"
       subtitle={'Recorded spending for '+format(month,'MMMM yyyy')}/>
     <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row 2xl:flex-col">
      <div className="grid h-[190px] w-[190px] shrink-0 place-items-center rounded-full"
        role="img" aria-label="Distribution of budgeted spending by category"
        style={{background:gradient}}>
       <div className="grid h-[126px] w-[126px] place-items-center rounded-full bg-white text-center">
        <div className="max-w-[116px]"><p className="truncate text-lg font-extrabold text-slate-950" title={money(fromCents(chartTotal))}>{money(fromCents(chartTotal))}</p>
         <p className="mt-0.5 text-xs text-slate-500">Total Spent</p></div>
       </div>
      </div>
      <div className="w-full min-w-0 flex-1 space-y-1.5">
       {chartRows.slice(0,7).map(row=><div key={row.id} className="flex min-w-0 items-center gap-2 border-b border-slate-100 py-1.5 text-xs">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{backgroundColor:row.color}}/>
        <span className="min-w-0 flex-1 truncate text-slate-700" title={row.category}>{row.category}</span>
        <strong className="shrink-0 text-slate-900">{money(row.spent)}</strong>
        <span className="w-10 shrink-0 text-right font-semibold text-slate-500">
          {chartTotal?(toCents(row.spent)/chartTotal*100).toFixed(1):0}%</span>
       </div>)}
       {chartRows.length>7&&<p className="text-xs text-slate-500">+ {chartRows.length-7} more budget categories</p>}
       {!chartRows.length&&<p className="py-6 text-center text-xs text-slate-500">No spending recorded against a budget this month.</p>}
      </div>
     </div>
   </section>
  </div>

  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
   <section className={card+' min-w-0 p-4 sm:p-5'}>
    <SectionHeading Icon={Fi.FiSun} title="Budget Insights" subtitle={'A quick look at your budget health for '+format(month,'MMMM yyyy')}/>
    <div className="mt-4 space-y-2.5">
     <button type="button" onClick={()=>setFilter('on-track')}
       className="flex w-full items-center gap-3 rounded-xl bg-emerald-50 p-3 text-left transition hover:bg-emerald-100">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700"><Fi.FiCheckCircle size={22}/></span>
      <span className="min-w-0 flex-1"><strong className="block text-sm text-slate-900">{onTrack.length} {onTrack.length===1?'category is':'categories are'} on track</strong>
       <span className="mt-0.5 block text-xs text-slate-600">{onTrack.length?onTrack.slice(0,3).map(x=>x.category).join(', ')+' within budget.':'No category currently has on-track status.'}</span></span>
      <Fi.FiChevronRight className="shrink-0 text-[#385575]"/>
     </button>
     <button type="button" onClick={()=>setFilter('near')}
       className="flex w-full items-center gap-3 rounded-xl bg-orange-50 p-3 text-left transition hover:bg-orange-100">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-orange-100 text-orange-700"><Fi.FiAlertTriangle size={22}/></span>
      <span className="min-w-0 flex-1"><strong className="block text-sm text-slate-900">{warning.length} {warning.length===1?'category is':'categories are'} near the limit</strong>
       <span className="mt-0.5 block text-xs text-slate-600">{warning.length?warning.slice(0,2).map(x=>x.category+' at '+Math.round(x.percentage)+'%').join(', '):'No category has reached the configured warning threshold.'}</span></span>
      <Fi.FiChevronRight className="shrink-0 text-[#385575]"/>
     </button>
     <button type="button" onClick={()=>setFilter('at-risk')}
       className="flex w-full items-center gap-3 rounded-xl bg-rose-50 p-3 text-left transition hover:bg-rose-100">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-rose-100 text-rose-700"><Fi.FiTrendingUp size={22}/></span>
      <span className="min-w-0 flex-1"><strong className="block text-sm text-slate-900">{critical.length} {critical.length===1?'category is':'categories are'} over or forecast to exceed budget</strong>
       <span className="mt-0.5 block text-xs text-slate-600">{critical.length?critical.slice(0,2).map(x=>x.category+' ('+Math.round(x.projectedRatio*100)+'% projected)').join(', '):'No saved category is currently forecast over its limit.'}</span></span>
      <Fi.FiChevronRight className="shrink-0 text-[#385575]"/>
     </button>
    </div>
    <button type="button" onClick={()=>setShowAlertDetails(value=>!value)} aria-expanded={showAlertDetails}
      className="mt-4 flex min-h-10 w-full items-center justify-between rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:bg-slate-50">
      How are budget alerts calculated? {showAlertDetails?<Fi.FiChevronUp/>:<Fi.FiChevronDown/>}</button>
    {showAlertDetails&&<p className="mt-2 text-xs leading-relaxed text-slate-600">
      Near limit starts at the configured budget warning percentage (currently {settings.budgetWarningPercent||80}%).
      Forecasts consider recorded spending, time elapsed and scheduled accruals. An alert is an estimate, not a prediction of an actual bank payment.
     </p>}
   </section>

   <section className={card+' min-w-0 p-4 sm:p-5'}>
    <SectionHeading Icon={Fi.FiClock} title="Recent Budget Activity"
      subtitle="Latest expense activity that affects your budget period"
      action={<Link to="/transactions" className="rounded-lg px-2 py-2 text-xs font-bold text-[#0176D3] hover:bg-blue-50">View All</Link>}/>
    <div className="mt-4 hidden overflow-x-auto sm:block">
      <table className="w-full min-w-[480px] border-collapse text-left text-xs" aria-label="Recent budget activity">
       <thead className="bg-[#f2f6fb] text-[10px] font-bold uppercase tracking-wide text-slate-600">
        <tr><th scope="col" className="px-2.5 py-2.5">Date</th><th scope="col" className="px-2.5 py-2.5">Description</th>
         <th scope="col" className="px-2.5 py-2.5">Category</th><th scope="col" className="px-2.5 py-2.5 text-right">Amount</th>
         <th scope="col" className="px-2.5 py-2.5 text-right">Budget Used</th></tr>
       </thead>
       <tbody>{activity.map((tx,index)=>{
        const budget=matchBudget(tx);
        return <tr key={String(tx.id||index)+'-row'} className="border-b border-slate-100">
         <td className="whitespace-nowrap px-2.5 py-3 text-slate-600">{shortDate(tx.date)}</td>
         <td className="max-w-40 px-2.5 py-3"><strong className="block truncate text-slate-900" title={tx.payee||tx.category||'Expense'}>{tx.payee||tx.category||'Expense'}</strong></td>
         <td className="max-w-28 px-2.5 py-3"><span className="inline-block max-w-full truncate rounded-full bg-blue-50 px-2 py-1 text-sky-800">{tx.category||'Other'}</span></td>
         <td className="whitespace-nowrap px-2.5 py-3 text-right font-bold text-rose-600">-{money(tx.amount)}</td>
         <td className="whitespace-nowrap px-2.5 py-3 text-right">{budget
          ?<span className={'rounded-full px-2 py-1 text-[11px] font-bold '+usageTone(budget)}>{budget.limit>0?Math.round(budget.percentage)+'%':'N/A'}</span>
          :<span className="text-slate-400">No budget</span>}</td>
        </tr>;
       })}</tbody>
      </table>
    </div>
    <div className="mt-4 space-y-2 sm:hidden">{activity.map((tx,index)=>{
      const budget=matchBudget(tx);
      return <div key={String(tx.id||index)+'-mobile'} className="flex items-center gap-3 border-b border-slate-100 py-2">
       <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-50 text-[#0176D3]"><Fi.FiShoppingBag size={17}/></span>
       <div className="min-w-0 flex-1"><p className="truncate text-xs font-bold text-slate-900">{tx.payee||tx.category||'Expense'}</p>
        <p className="mt-1 text-[11px] text-slate-500">{shortDate(tx.date)} · {tx.category||'Other'}</p></div>
       <div className="text-right"><p className="text-xs font-bold text-rose-600">-{money(tx.amount)}</p>
        {budget&&<p className="mt-1 text-[11px] text-slate-500">{Math.round(budget.percentage)}% of budget used</p>}</div>
      </div>;
    })}</div>
    {!activity.length&&<p className="py-10 text-center text-sm text-slate-500">No recorded expense activity for this period.</p>}
   </section>
  </div>

  {showAddForm&&<AddBudgetForm onClose={()=>setShowAddForm(false)} onSave={async budget=>{
    await actions.addBudget(budget);
    setShowAddForm(false);
  }}/>}
 </motion.div>;
}

// Add Budget Form Component
const AddBudgetForm = ({ onClose, onSave }) => {
  const { state } = useApp();
  const { categories, settings } = state;
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    period: 'monthly',
    startDate: '',
    endDate: '',
    notes: '',
  });

  const expenseCategories = categories.filter(c => c.type === 'expense');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.category || !(Number(formData.amount) > 0) ||
      (formData.period === 'custom' && (!formData.startDate || !formData.endDate || formData.endDate < formData.startDate))) {
      return;
    }

    const budget = {
      ...formData,
      amount: parseFloat(formData.amount),
      id: Date.now(),
      createdAt: new Date(),
    };

    onSave(budget);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Budget</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Select category</option>
              {expenseCategories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period *
              </label>
              <select
                value={formData.period}
                onChange={(e) => handleChange('period', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {formData.period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-gray-700">Start date
                <input type="date" required value={formData.startDate}
                  onChange={e => handleChange('startDate', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </label>
              <label className="text-sm text-gray-700">End date
                <input type="date" required min={formData.startDate} value={formData.endDate}
                  onChange={e => handleChange('endDate', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </label>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional notes about this budget..."
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.category || !(Number(formData.amount) > 0) ||
              (formData.period === 'custom' && (!formData.startDate || !formData.endDate || formData.endDate < formData.startDate))}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
          >
            Add Budget
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};